// @soft-depth
import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { coverQuizSignals, coverReasonMessageId } from '../../../views/CoverQuiz/coverQuizData';
import { quizLangOf } from '../../../views/CoverQuiz/quizLang';
import { useCoverQuiz } from '../../../views/CoverQuiz/coverQuizStorage';
import { CLIENT_OBJECTIONS, inferObjections, useMarkedObjections } from './clientObjections';
import { useCanEditClientNotes } from '../../../views/Advisor/ClientV2/clientNotesStore';
import type { ClientObjectionId, ObjectionFacts } from './clientObjections';
import { DiscLine, IdeaEditButton } from './IdeaKit';
import { discLabel, isDiscLetter } from '../../../constants/disc';
import SoftDepthScope from '../../../design-system/SoftDepthScope';

/**
 * 異議處理 — the 化解異議 step of Smart IDEA's 會談流程. Rules and storage:
 * ./clientObjections.ts.
 *
 * Every row reads at a glance — objection → answer, then the line to say.
 * Nothing hides behind a toggle: this is read minutes before a meeting, not
 * practised. 編輯 (ObjectionEditButton, in the step's head) opens only the
 * chips that mark what the client said; the step owns that state.
 *
 * With the client's DISC type on file, each answer gains a line on how to
 * deliver it to that type (`smart-idea-objection-<id>-disc-<letter>`).
 */

type IntlLike = {
  formatMessage: (descriptor: { id: string }, values?: Record<string, unknown>) => string;
  locale?: string;
};

export interface ObjectionListProps {
  clientId?: string | null;
  facts: ObjectionFacts;
  intl: IntlLike;
  /** Shows the chips that mark what the client said. */
  editing?: boolean;
  /** The client's DISC letter; adds a delivery line to each answer. */
  disc?: string | null;
}

export const ObjectionEditButton = (props: { editing: boolean; onToggle: () => void; intl: IntlLike }) => (
  // The same 編輯 as 招募 IDEA's 探索 / 化解 steps.
  <IdeaEditButton
    pressed={props.editing}
    testId="objection-edit"
    onClick={props.onToggle}
    label={props.intl.formatMessage({
      id: props.editing ? 'smart-idea-objections-done' : 'smart-idea-objections-edit'
    })}
  />
);

const ObjectionRow = (props: {
  id: ClientObjectionId;
  t: (id: string, values?: Record<string, unknown>) => string;
  inferred?: boolean;
  children?: React.ReactNode;
  disc?: string | null;
  locale?: string;
}) => {
  const { id, t, inferred, disc, locale } = props;
  return (
    // 可能會提 is a guess, and its dashed amber outline says so; 客戶提過 is a fact.
    <Stack
      data-testid="objection-row"
      data-objection={id}
      data-inferred={inferred || undefined}
      sx={{
        gap: 0.75,
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        border: '1px solid',
        borderStyle: inferred ? 'dashed' : 'solid',
        borderColor: inferred ? 'warning.border' : 'divider',
        bgcolor: 'background.paper'
      }}
    >
      {props.children}
      <Typography
        component="div"
        variant="body2"
        sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px', color: 'grey.800' }}
      >
        <Box component="strong" sx={{ fontWeight: 700 }}>
          {t(`smart-idea-objection-${id}`)}
        </Box>
        <Box component="span" aria-hidden="true" sx={{ color: 'text.disabled' }}>
          →
        </Box>
        <span>{t(`smart-idea-objection-${id}-answer`)}</span>
      </Typography>
      {isDiscLetter(disc) && (
        <DiscLine
          letter={disc}
          prefix={t('clientv2-idea-disc-line', { type: discLabel(disc, locale) })}
          text={t(`smart-idea-objection-${id}-disc-${disc}`)}
          testId="objection-disc-line"
        />
      )}
      <Typography component="p" variant="body2" sx={{ m: 0, color: 'primary.dark' }}>
        {t('smart-idea-objections-say')}
        {t(`smart-idea-objection-${id}-script`)}
      </Typography>
    </Stack>
  );
};

/** 客戶提過 / 可能會提 — a dot, the group's name and a muted count or hint. */
const GroupHead = (props: { tone: 'marked' | 'inferred'; title: string; hint: string }) => (
  <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, mt: 0.5 }}>
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        flexShrink: 0,
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: props.tone === 'marked' ? 'grey.800' : 'warning.main'
      }}
    />
    <Typography component="strong" variant="body2" sx={{ fontWeight: 700, color: 'grey.800' }}>
      {props.title}
    </Typography>
    <Typography component="small" variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
      {props.hint}
    </Typography>
  </Stack>
);

const EmptyNote = (props: { children: React.ReactNode; testId?: string }) => (
  <Typography
    component="p"
    variant="body2"
    data-testid={props.testId}
    sx={{
      m: 0,
      px: 1.25,
      py: 1,
      borderRadius: 1.25,
      border: '1px dashed',
      borderColor: 'divider',
      color: 'text.secondary'
    }}
  >
    {props.children}
  </Typography>
);

const WarningNote = (props: { children: React.ReactNode; testId?: string }) => (
  <Typography
    component="p"
    variant="subtitle2"
    data-testid={props.testId}
    sx={{ m: 0, fontWeight: 500, color: 'warning.dark' }}
  >
    {props.children}
  </Typography>
);

export const ObjectionList = (props: ObjectionListProps) => {
  const { clientId, facts, intl, editing, disc } = props;
  const t = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);
  const [marked, setMarked] = useMarkedObjections(clientId);
  // No factsheet, nowhere to save a mark: the chips cannot be tapped, and the
  // line under 客戶提過 says why — on V1 it is the only place that does.
  const canSave = useCanEditClientNotes(clientId);
  const quiz = useCoverQuiz(clientId);
  const quizObjections = quiz
    ? coverQuizSignals(quiz.answers, quizLangOf(intl.locale)).objections.map(o => ({
        id: o.id,
        reasons: o.reasons.map(coverReasonMessageId)
      }))
    : [];
  const inferred = inferObjections(facts, quizObjections, marked);
  const markedInOrder = CLIENT_OBJECTIONS.filter(id => marked.includes(id));
  const spouseShown = marked.includes('spouse') || inferred.some(o => o.id === 'spouse');

  return (
    <SoftDepthScope mode="business">
      <Stack data-testid="objection-card" sx={{ gap: 1 }}>
        {editing && (
          <Stack
            sx={{
              gap: 1,
              px: 1.5,
              py: 1.25,
              borderRadius: 1.5,
              border: '1px dashed',
              borderColor: 'divider',
              bgcolor: 'grey.50'
            }}
          >
            <Typography component="span" variant="subtitle2" sx={{ fontWeight: 700, color: 'grey.600' }}>
              {t('smart-idea-objections-pick')}
            </Typography>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {CLIENT_OBJECTIONS.map(id => {
                const on = marked.includes(id);
                return (
                  <Chip
                    key={id}
                    component="button"
                    type="button"
                    clickable
                    variant={on ? 'filled' : 'outlined'}
                    color={on ? 'primary' : 'default'}
                    aria-pressed={on}
                    data-testid="objection-chip"
                    data-objection={id}
                    disabled={!canSave}
                    onClick={() => setMarked(on ? marked.filter(m => m !== id) : [...marked, id])}
                    label={t(`smart-idea-objection-${id}`)}
                  />
                );
              })}
            </Stack>
          </Stack>
        )}

        <GroupHead
          tone="marked"
          title={t('smart-idea-objections-marked')}
          hint={t('smart-idea-objections-marked-hint', { count: markedInOrder.length })}
        />
        {markedInOrder.length ? (
          markedInOrder.map(id => <ObjectionRow key={id} id={id} t={t} disc={disc} locale={intl.locale} />)
        ) : (
          <EmptyNote testId="objection-marked-empty">{t('smart-idea-objections-marked-empty')}</EmptyNote>
        )}
        {clientId && !canSave && (
          <WarningNote testId="objection-readonly">{t('clientv2-smart-idea-no-factsheet')}</WarningNote>
        )}

        <GroupHead
          tone="inferred"
          title={t('smart-idea-objections-inferred')}
          hint={t('smart-idea-objections-inferred-hint')}
        />
        {inferred.length ? (
          inferred.map(o => (
            <ObjectionRow key={o.id} id={o.id} t={t} inferred disc={disc} locale={intl.locale}>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                {o.reasons.map((r, i) => (
                  // Where the guess comes from: the factsheet (📄) or the 保障小測驗 (📋).
                  <Chip
                    key={i}
                    size="small"
                    variant="outlined"
                    color={r.source === 'quiz' ? 'primary' : 'warning'}
                    data-testid="objection-reason"
                    data-source={r.source}
                    sx={{ height: 'auto', minHeight: 24, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
                    label={`${r.source === 'quiz' ? '📋 ' : '📄 '}${t(
                      r.messageId,
                      r.parts
                        ? { ...r.values, why: r.parts.map(p => t(p)).join(t('smart-idea-objection-why-separator')) }
                        : r.values
                    )}`}
                  />
                ))}
              </Stack>
            </ObjectionRow>
          ))
        ) : (
          <EmptyNote>{t('smart-idea-objections-inferred-empty')}</EmptyNote>
        )}

        {spouseShown && <WarningNote>{t('smart-idea-objections-spouse-note')}</WarningNote>}
      </Stack>
    </SoftDepthScope>
  );
};
